// New Goal — formulário que cria um goal conforme o Contrato de
// docs/screens/08-new-goal.md: milestone no GitHub (objetivo + constraints),
// issue de planejamento dentro dele, time no LiteLLM com o teto de gasto e
// chaves efêmeras com modelo por papel e expiração pelo wall-clock.
//
// Fora daqui, por decisão do contrato (sem dono nativo): aba "Start from
// spec", select "Execution" e input "Stuck threshold (iterations)".

import { repoAtual, gateway, litellm, SourceError, type Agent } from "../api";
import { errorBox, esc, head, skeleton, tabs, toast } from "../ui";

const CSS = `
.goal-row { display: flex; gap: 14px; }
.goal-row .field { flex: 1; }
.goal-actions { display: flex; align-items: center; gap: 12px; margin-top: 4px; }
`;
if (!document.getElementById("css-goal-form")) {
  const st = document.createElement("style");
  st.id = "css-goal-form";
  st.textContent = CSS;
  document.head.append(st);
}


/** Rota da área: #/goals/new. Sub-rota única — o hash é lido só para confirmar
 *  que estamos nela; qualquer outra coisa cai no formulário mesmo assim. */
export async function goalForm(el: HTMLElement): Promise<void> {
  // Um único "Cancel", no rodapé do formulário — é o que o frame mostra.
  const header = head({ title: "New Goal" });
  el.innerHTML = header + skeleton();

  try {
    const [agents, models] = await Promise.all([litellm.agents(), litellm.models()]);
    // Runtime de um goal é sessão de agente: só modelo de texto. A allowlist
    // do gateway traz whisper e glimmer junto, e sem filtro o formulário abre
    // roteando o goal para um modelo de transcrição.
    const texto = models.map((m) => m.id).filter((id) => !/whisper|glimmer/i.test(id));
    el.innerHTML = header + form(agents, texto.length ? texto : models.map((m) => m.id));
    wire(el);
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML =
      header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}

const options = (xs: string[]): string =>
  xs.map((x) => `<option value="${esc(x)}">${esc(x)}</option>`).join("");

function form(agents: Agent[], models: string[]): string {
  return (
    tabs([{ label: "Describe objective", href: "#/goals/new", ativa: true }]) +
    `<form class="card" id="goal-form">
      <div class="field">
        <label class="label" for="g-obj">Objective</label>
        <textarea class="textarea" id="g-obj" name="objective" rows="4" required
          placeholder="Describe the outcome you want. Agents will explore, plan a Definition of Done for your approval, then loop until it's met."></textarea>
      </div>
      <div class="field">
        <label class="label" for="g-con">Constraints &amp; preferences (optional)</label>
        <!-- Vai junto na descrição do milestone — a task de planejamento lê de
             lá. Observação do contrato, não texto de tela: o frame não tem
             helper aqui. -->
        <textarea class="textarea" id="g-con" name="constraints" rows="4"></textarea>
      </div>
      <div class="field">
        <label class="label" for="g-agent">Planning agent (optional)</label>
        <select class="input" id="g-agent" name="agent">
          <option value="">No agent (manual task)</option>
          ${options(agents.map((a) => a.agent_name))}
        </select>
        <span class="hint">Runs the first (planning) task. Pick an agent with the repo attached — the default agent has no repo access. Leave unset to use the org default agent.</span>
      </div>
      <div class="goal-row">
        <div class="field">
          <label class="label" for="g-plan">Plan agent runtime</label>
          <select class="input" id="g-plan" name="plan_model" required>${options(models)}</select>
          <span class="hint">Every plan-writing/review session runs Claude on the runner. The goal router stays on Claude.</span>
        </div>
        <div class="field">
          <label class="label" for="g-worker">Worker runtime</label>
          <select class="input" id="g-worker" name="worker_model" required>${options(models)}</select>
          <span class="hint">Work sessions run Grok on the runner (model from the daemon, default grok-4.6). Falls back to Claude cloud when the runner is busy, offline, or rate-limited.</span>
        </div>
      </div>
      <div class="goal-row">
        <div class="field">
          <label class="label" for="g-spend">Spend cap (USD)</label>
          <!-- Vira max_budget do time no LiteLLM; sem helper na tela (frame). -->
          <input class="input" id="g-spend" name="spend" type="number" min="0" step="1" value="50" />
        </div>
        <div class="field">
          <label class="label" for="g-hours">Wall-clock limit (hours)</label>
          <!-- Vira duration das chaves do goal; sem helper na tela (frame). -->
          <input class="input" id="g-hours" name="hours" type="number" min="0" step="1" value="24" />
        </div>
      </div>
      <div class="goal-actions">
        <button class="btn btn-primary" type="submit">Create goal</button>
        <a class="btn" href="#/goals">Cancel</a>
      </div>
    </form>`
  );
}

function wire(el: HTMLElement): void {
  const form = el.querySelector<HTMLFormElement>("#goal-form")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const d = new FormData(form);
    const objective = String(d.get("objective") ?? "").trim();
    const constraints = String(d.get("constraints") ?? "").trim();
    const agent = String(d.get("agent") ?? "").trim();
    const planModel = String(d.get("plan_model") ?? "").trim();
    const workerModel = String(d.get("worker_model") ?? "").trim();
    const spend = Number(d.get("spend") ?? 0);
    const hours = Number(d.get("hours") ?? 0);

    if (!objective) return toast("Descreva o resultado que você quer");
    if (!planModel || !workerModel)
      return toast("Nenhum modelo no gateway — cadastre um em /model/new antes");

    const botao = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    botao.disabled = true;
    try {
      const repo = await repoAtual();

      // Milestone: primeira linha vira o título; o resto + constraints, a descrição.
      const linhas = objective.split("\n");
      const title = (linhas[0] ?? "").trim();
      const description = [linhas.slice(1).join("\n").trim(), constraints]
        .filter(Boolean)
        .join("\n\n");
      const ms = await gateway<{ number: number }>(`github/repos/${repo}/milestones`, {
        method: "POST",
        body: JSON.stringify({ title, description }),
      });

      // Issue de planejamento dentro do milestone. Agente escolhido vira label
      // (mecanismo papel:* do brief); sem escolha = sem label = agente default.
      // Label é identificador, como em api.ts: "Senior Dev" vira "senior-dev".
      const papel = agent.toLowerCase().replace(/\s+/g, "-");
      await gateway(`github/repos/${repo}/issues`, {
        method: "POST",
        body: JSON.stringify({
          title: `Plan: ${title}`,
          milestone: ms.number,
          labels: papel ? [`papel:${papel}`] : [],
        }),
      });

      // Um time por goal, com o teto de gasto — o gateway corta sozinho.
      const team = await gateway<{ team_id: string }>("litellm/team/new", {
        method: "POST",
        body: JSON.stringify({
          team_alias: `goal-${ms.number}`,
          ...(spend > 0 ? { max_budget: spend } : {}),
        }),
      });

      // Chaves efêmeras por papel: modelo escolhido + expiração pelo wall-clock.
      const chave = (model: string, papel: string) =>
        gateway("litellm/key/generate", {
          method: "POST",
          body: JSON.stringify({
            team_id: team.team_id,
            models: [model],
            key_alias: `goal-${ms.number}-${papel}`,
            ...(hours > 0 ? { duration: `${hours}h` } : {}),
          }),
        });
      await Promise.all([chave(planModel, "plan"), chave(workerModel, "worker")]);

      toast(`Goal criado como milestone #${ms.number}`);
      location.hash = "/goals";
    } catch (err) {
      toast((err as SourceError).message);
      botao.disabled = false;
    }
  });
}
